import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Sparkles, Loader2 } from "lucide-react";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const YEARS = Array.from({ length: 100 }, (_, i) => new Date().getFullYear() - i);

const DAYS_IN_MONTH = (m, y) => new Date(y, m, 0).getDate();

export const OnboardingModal = ({ open, onComplete }) => {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    preferred_name: "",
    birth_year: "",
    birth_month: "",
    birth_day: "",
    birth_time: "12:00",
    birth_city: "",
  });

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const canNext = () => {
    if (step === 0) return form.preferred_name.trim().length >= 1;
    if (step === 1) return form.birth_year && form.birth_month && form.birth_day;
    if (step === 2) return /^\d{2}:\d{2}$/.test(form.birth_time);
    if (step === 3) return form.birth_city.trim().length >= 2;
    return false;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        preferred_name: form.preferred_name.trim(),
        birth_year: parseInt(form.birth_year),
        birth_month: parseInt(form.birth_month),
        birth_day: parseInt(form.birth_day),
        birth_time: form.birth_time,
        birth_city: form.birth_city.trim(),
      };
      const { data } = await api.post("/onboarding", payload);
      toast.success(`Welcome, ${form.preferred_name}. You are a ${data.human_design.type}.`);
      onComplete?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save your blueprint");
    } finally {
      setSubmitting(false);
    }
  };

  const next = () => {
    if (step < 3) setStep(step + 1);
    else submit();
  };

  const titles = [
    "What name shall we call you?",
    "When were you born?",
    "At what time?",
    "And where on Earth?",
  ];

  return (
    <Dialog open={open}>
      <DialogContent
        className="bg-[#0A0E17] border border-white/10 text-[#F8FAFC] sm:max-w-[520px] shadow-[0_0_60px_rgba(212,175,55,0.15)] [&>button[aria-label='Close']]:hidden"
        data-testid="onboarding-modal"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">Onboarding</DialogTitle>
        <DialogDescription className="sr-only">Tell us about your birth.</DialogDescription>

        <div className="flex items-center gap-2 text-[#D4AF37] mb-1">
          <Sparkles size={16} strokeWidth={1.5} />
          <span className="text-xs uppercase tracking-[0.25em] font-semibold">
            Step {step + 1} of 4
          </span>
        </div>
        <h2 className="font-serif-mystic text-3xl sm:text-4xl tracking-tight mb-2">
          {titles[step]}
        </h2>
        <p className="text-sm text-[#94A3B8] mb-6">
          Your cosmic blueprint is calculated from the moment you arrived.
        </p>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="space-y-4"
          >
            {step === 0 && (
              <div>
                <Label htmlFor="pn" className="text-[#94A3B8] uppercase tracking-[0.15em] text-xs">
                  Preferred name
                </Label>
                <Input
                  id="pn"
                  data-testid="onboarding-name-input"
                  autoFocus
                  value={form.preferred_name}
                  onChange={(e) => update("preferred_name", e.target.value)}
                  placeholder="Luna"
                  className="mt-2 bg-[#05070A]/60 border-white/10 text-white placeholder-white/30 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]"
                />
              </div>
            )}

            {step === 1 && (
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-[#94A3B8] uppercase tracking-[0.15em] text-xs">Month</Label>
                  <Select value={form.birth_month} onValueChange={(v) => update("birth_month", v)}>
                    <SelectTrigger data-testid="onboarding-month-trigger" className="mt-2 bg-[#05070A]/60 border-white/10 text-white">
                      <SelectValue placeholder="Month" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0E17] text-white border-white/10 max-h-72">
                      {MONTHS.map((m, i) => (
                        <SelectItem key={m} value={String(i + 1)} data-testid={`month-${i + 1}`}>
                          {m}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#94A3B8] uppercase tracking-[0.15em] text-xs">Day</Label>
                  <Select value={form.birth_day} onValueChange={(v) => update("birth_day", v)}>
                    <SelectTrigger data-testid="onboarding-day-trigger" className="mt-2 bg-[#05070A]/60 border-white/10 text-white">
                      <SelectValue placeholder="Day" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0E17] text-white border-white/10 max-h-72">
                      {Array.from(
                        {
                          length: DAYS_IN_MONTH(
                            parseInt(form.birth_month || 1),
                            parseInt(form.birth_year || 2000),
                          ),
                        },
                        (_, i) => i + 1,
                      ).map((d) => (
                        <SelectItem key={d} value={String(d)} data-testid={`day-${d}`}>
                          {d}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#94A3B8] uppercase tracking-[0.15em] text-xs">Year</Label>
                  <Select value={form.birth_year} onValueChange={(v) => update("birth_year", v)}>
                    <SelectTrigger data-testid="onboarding-year-trigger" className="mt-2 bg-[#05070A]/60 border-white/10 text-white">
                      <SelectValue placeholder="Year" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0E17] text-white border-white/10 max-h-72">
                      {YEARS.map((y) => (
                        <SelectItem key={y} value={String(y)} data-testid={`year-${y}`}>
                          {y}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {step === 2 && (
              <div>
                <Label htmlFor="bt" className="text-[#94A3B8] uppercase tracking-[0.15em] text-xs">
                  Birth time (24h)
                </Label>
                <Input
                  id="bt"
                  type="time"
                  data-testid="onboarding-time-input"
                  value={form.birth_time}
                  onChange={(e) => update("birth_time", e.target.value)}
                  className="mt-2 bg-[#05070A]/60 border-white/10 text-white focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]"
                />
                <p className="text-xs text-[#64748B] mt-2">
                  If unknown, 12:00 still produces a meaningful chart.
                </p>
              </div>
            )}

            {step === 3 && (
              <div>
                <Label htmlFor="bc" className="text-[#94A3B8] uppercase tracking-[0.15em] text-xs">
                  Birth city
                </Label>
                <Input
                  id="bc"
                  data-testid="onboarding-city-input"
                  autoFocus
                  value={form.birth_city}
                  onChange={(e) => update("birth_city", e.target.value)}
                  placeholder="Lisbon, Portugal"
                  className="mt-2 bg-[#05070A]/60 border-white/10 text-white placeholder-white/30 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]"
                />
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-between mt-8">
          <button
            disabled={step === 0 || submitting}
            onClick={() => setStep(Math.max(0, step - 1))}
            className="text-sm text-[#94A3B8] hover:text-white disabled:opacity-30 transition-colors"
            data-testid="onboarding-back-btn"
          >
            ← Back
          </button>
          <Button
            disabled={!canNext() || submitting}
            onClick={next}
            data-testid="onboarding-next-btn"
            className="bg-[#D4AF37] text-[#05070A] hover:bg-[#F3E5AB] transition-colors px-6 py-2 font-medium tracking-wide shadow-[0_0_15px_rgba(212,175,55,0.35)]"
          >
            {submitting ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" />
                Reading the stars
              </>
            ) : step === 3 ? (
              "Reveal My Chart"
            ) : (
              "Continue"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
